import { Link, useParams } from "react-router-dom";
import { getContract } from "../api/index.api";
import { useEffect, useState } from "react";
import { Contract } from "../interfaces/Contracts.interface";
import LTemplate1 from "../../../components/loading/template1";
import { Button, Card, CardBody, CardHeader, Chip, Divider } from "@nextui-org/react";
import { ContractStatus } from "../types/Contracts.type";
import { currencyFormat, numFormat } from "../../../auth/data/tools";
import PDFViewer from "../layouts/PDFViewer";
import { ArrowBarLeft, FileEarmarkPersonFill } from "react-bootstrap-icons";
import { useAuth } from "../../../context/AuthContext";
import { validateToken } from "../../../login/login";

const ContractView = () => {
    const { id } = useParams();
    const { userInfo, setUserInfo, login } = useAuth();
    const [contract, setContract] = useState<Contract|null>(null);
    const [loading, setLoading] = useState(true);
    const [openModal, setOpenModal] = useState(false);
    const [viewFile, setViewFile] = useState("");

    useEffect(() => {
        if(id)
        {
            viewContract(id);
        }

        return () => {};
    }, [id]);

    const viewContract = async(id: string) => {
        setLoading(true);
        const user = await validateToken(userInfo, setUserInfo, login);
        const result = await getContract(user, id);
        setContract(result);
        setLoading(false);
    }

    const onOpenFile = (url: string) => {
        setOpenModal(true);
        setViewFile(url);
    }

    const closeView = () => {
        setOpenModal(false);
        setViewFile("");
    }

    return(
        <>
            {
                loading
                ?
                    <LTemplate1 status="Obteniendo información"/>
                    :
                    (
                        <div className="my-14 lg:px-6 max-w-[95rem] mx-auto w-full flex flex-col gap-4">
                            <h3 className="flex gap-1 items-center text-xl font-semibold">
                                <Link to={"/sumaleasing/Contratos"} className="rounded-xl cursor-pointer transition-all duration-150 hover:bg-default-100 p-2">
                                    <ArrowBarLeft size={25}/>
                                </Link>
                                <span>Detalles del Contrato</span>
                            </h3>
                            {
                                contract
                                ?
                                    <>
                                        <ContractSummary contract={contract} onOpenFile={onOpenFile}/>
                                        <PDFViewer openModal={openModal} onClose={closeView} url={viewFile}/>
                                    </>
                                :
                                    <div className="flex align-center justify-center w-full">
                                        <p>Este número de contrato no está registrado en el sistema o no pertenece al cliente.</p>
                                    </div>
                            }
                        </div>
                    )
            }
        </>
    );
}

const ContractSummary:React.FC<{contract: Contract, onOpenFile: (url: string) => void}> = ({contract, onOpenFile}) => {
    const { userInfo } = useAuth();
    const dec = userInfo.decimals;

    return (
        <div className="flex w-full gap-7 justify-evenly flex-wrap">
            <Card className="w-1/2 max-w-[500px] px-4 py-2">
                <CardHeader className="flex flex-col items-start gap-3">
                <h2 className="text-lg">Información General</h2>
                </CardHeader>
                <Divider />
                <CardBody className="flex flex-col items-start gap-3">
                    <p className="mb-3 text-sm">{`Cliente: ${contract.name}`}</p>
                    <p className="mb-3 text-sm">{`Usuario: ${contract.username ? contract.username : "-"}`}</p>
                    <p className="mb-3 text-sm">{`Estatus: `}{getStatus(contract.status)}</p>
                    <p className="mb-3 text-sm">{`Fecha primera factura de renta: ${contract.first_rent_invoice ?? "-"}`}</p>
                    <p className="mb-3 text-sm">{`Fecha última factura de renta: ${contract.last_rent_invoice ?? "-"}`}</p>
                </CardBody>
            </Card>
            <Card className="w-1/2 max-w-[500px] px-4 py-2">
                <CardHeader className="flex flex-col items-start gap-3">
                <h2 className="text-lg">Arrendamiento Mensual</h2>
                </CardHeader>
                <Divider />
                <CardBody className="flex flex-col items-start gap-3">
                    <p className="mb-3">{`Renta mensual (S/IVA): ${currencyFormat(contract.monthly_rent, dec)}`}</p>
                    <p className="mb-3">{`Seguro mensual (S/IVA): ${currencyFormat(contract.monthly_insurance, dec)}`}</p>
                    <p className="mb-3">{`Trámites mensual (S/IVA): ${currencyFormat(contract.monthly_transaction, dec)}`}</p>
                    <p className="mb-3">{`Total mensual (S/IVA): ${currencyFormat(contract.monthly_total, dec)}`}</p>
                </CardBody>
            </Card>
            <Card className="w-1/2 max-w-[500px] px-4 py-2">
                <CardHeader className="flex flex-col items-start gap-3">
                <h2 className="text-lg">Valor Residual y Deposito en garantía</h2>
                </CardHeader>
                <Divider />
                <CardBody className="flex flex-col items-start gap-3">
                    <p className="mb-3">{`Valor residual ($): ${currencyFormat(contract.residual_amount, dec)}`}</p>
                    <p className="mb-3">{`Valor residual (%): ${numFormat(contract.perc_residual_amount, dec)}%`}</p>
                    <p className="mb-3">{`Tipo de residual: ${contract.residual_type}`}</p>
                    <p className="mb-3">{`Depósito en garantía: ${currencyFormat(contract.deposito_garantia_amount, dec)}`}</p>
                </CardBody>
            </Card>
            <Card className="w-1/2 max-w-[500px] px-4 py-2">
                <CardHeader className="flex flex-col items-start gap-3">
                <h2 className="text-lg">Archivos adjuntos</h2>
                </CardHeader>
                <Divider />
                <CardBody className="flex flex-col items-start gap-3">
                    {
                        (
                            !contract.orden_requisicion_file &&
                            !contract.contrato_file &&
                            !contract.pagare_file &&
                            !contract.deposito_garantia_file &&
                            !contract.deposito_garantia_usuario_file &&
                            !contract.carta_promesa_file &&
                            !contract.pdf_factura_file &&
                            !contract.asset_file_seguro &&
                            !contract.asset_file_tarjeta_circulacion &&
                            !contract.asset_file_primera_verificacion
                        ) ?
                            <p className="self-center">No hay archivos disponibles</p>
                        :
                            <>
                                {contract.carta_promesa_file             && <p className="flex gap-2 items-center mb-3"><OpenFile openFile={onOpenFile} url={contract.carta_promesa_file}/>{`Carta promesa`}</p>}
                                {contract.contrato_file                  && <p className="flex gap-2 items-center mb-3"><OpenFile openFile={onOpenFile} url={contract.contrato_file}/>{`Contrato`}</p>}
                                {contract.deposito_garantia_file         && <p className="flex gap-2 items-center mb-3"><OpenFile openFile={onOpenFile} url={contract.deposito_garantia_file}/>{`Depósito en garantía`}</p>}
                                {contract.deposito_garantia_usuario_file && <p className="flex gap-2 items-center mb-3"><OpenFile openFile={onOpenFile} url={contract.deposito_garantia_usuario_file}/>{`Depósito en garantía usuario`}</p>}
                                {contract.orden_requisicion_file         && <p className="flex gap-2 items-center mb-3"><OpenFile openFile={onOpenFile} url={contract.orden_requisicion_file}/>{`Orden de requisición`}</p>}
                                {contract.pagare_file                    && <p className="flex gap-2 items-center mb-3"><OpenFile openFile={onOpenFile} url={contract.pagare_file}/>{`Pagaré`}</p>}
                                
                                {contract.pdf_factura_file                    && <p className="flex gap-2 items-center mb-3"><OpenFile openFile={onOpenFile} url={contract.pdf_factura_file}/>{`Factura`}</p>}
                                {contract.asset_file_seguro                   && <p className="flex gap-2 items-center mb-3"><OpenFile openFile={onOpenFile} url={contract.asset_file_seguro}/>{`Póliza seguro`}</p>}
                                {contract.asset_file_tarjeta_circulacion      && <p className="flex gap-2 items-center mb-3"><OpenFile openFile={onOpenFile} url={contract.asset_file_tarjeta_circulacion}/>{`Tarjeta Circulación`}</p>}
                                {contract.asset_file_primera_verificacion     && <p className="flex gap-2 items-center mb-3"><OpenFile openFile={onOpenFile} url={contract.asset_file_primera_verificacion}/>{`Talón verificación`}</p>}
                            </>
                    }
                </CardBody>
            </Card>
        </div>
    );
}

const getStatus = (status: ContractStatus):JSX.Element => {
    switch (status)
    {
        case "V":
            return <Chip color="success" size="sm">Vigente</Chip>;
        case "C":
            return <Chip color="danger" size="sm">Cancelado</Chip>;
        case "T":
            return <Chip color="primary" size="sm">Terminado</Chip>;
    }
}

const OpenFile:React.FC<{url: string, openFile: (url:string) => void}> = ({url, openFile}) => {
    return(
        <Button isIconOnly color="secondary" variant={"solid"} size="md" onPress={() => openFile(url)}>
            <FileEarmarkPersonFill size={20}/>
        </Button>
    );
}

export default ContractView;